import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useMemo, useState, useCallback } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';
import { useParams, getRouteApi, useNavigate, Link as RouterLink } from '@tanstack/react-router';
import {
  downloadBlob,
  createSortSchema,
  ACHelmetContainer,
  useSyncTableSearchParams,
  getTableModelsFromSearchParams,
} from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  Box,
  Card,
  Grid,
  Link,
  Alert,
  Stack,
  Button,
  Divider,
  Tooltip,
  useTheme,
  Skeleton,
  Typography,
  CardContent,
  useMediaQuery,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { fPriceString } from 'src/utils/format-string';

import { Item } from 'src/api/models/cart';
import { OrderIssueModel, BuyerOrderProductModel } from 'src/api/models/orders';
import {
  useGetCart,
  useAddToCart,
  useGetOrderDetails,
  useDownloadOrderPdf,
  useGetOrderIssuesForOrder,
} from 'src/api/hooks/api-hooks';

import { useSettingsContext } from 'src/components/settings';

import ActualWeightDialog from './actual-weight-dialog';
import ReportOrderIssueDialog from './report-order-issue-dialog';
import ReportProductIssueDialog from './report-product-issue-dialog';

export const OrderDetailsSearchParamsSchema = createSortSchema(['productName', 'price'] as const);

const routeApi = getRouteApi('/orders/$orderId');

interface ActionButtonProps {
  productsInCart?: Item[];
  product: BuyerOrderProductModel;
  handleAddToCart: (productStockId: string, quantity: number) => void;
  loading?: boolean;
}

const ActionButton = ({ productsInCart, product, handleAddToCart, loading }: ActionButtonProps) => {
  const isInCart = productsInCart?.some((item) => item.name === product.productName);
  const button = (
    <LoadingButton
      variant="outlined"
      onClick={() => handleAddToCart(product.productStockId, product.requestedQuantity)}
      loading={loading}
      disabled={isInCart}
    >
      {isInCart ? 'In Basket' : 'Add to Basket'}
    </LoadingButton>
  );

  return (
    <Box sx={{ minWidth: '120px' }}>
      {isInCart ? (
        <Tooltip title="Already in basket" arrow>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
    </Box>
  );
};

const ProductCard = ({
  product,
  isIssueLoggable,
  onReportProductIssue,
  reportProductIssueDialogOpen,
  orderId,
  issue,
  refetchOrderIssues,
  productsInCart,
  handleAddToCart,
  addToCartLoading,
  recalculatingLocalProducts,
}: any) => {
  const navigate = useNavigate();

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Link
                component="button"
                onClick={() =>
                  navigate({
                    to: paths.dashboard.products.productDetails(product.productStockId),
                  })
                }
              >
                <Typography variant="body1" color="textPrimary" align="left">
                  {product.productName}
                </Typography>
                <Typography variant="body1" color="GrayText" align="left">
                  {product.units}
                </Typography>
              </Link>
              <Typography variant="caption" color="text.secondary">
                {new Date().toLocaleDateString()}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Requested Quantity: {product.requestedQuantity}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Price: £{product.price}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Weight: {product.estimatedWeight ? `${product.estimatedWeight} kg` : 'N/A'}
              {/* {orderDetails && !params.row.receivedWeight && (
                <>
                  <Link
                    onClick={() => enterActualWeightDialogSetOpen(params.id.toString())}
                    sx={{ cursor: 'pointer' }}
                    color={theme.palette.primary.dark}
                  >
                    Enter actual weight
                  </Link>

                  <ActualWeightDialog
                    open={enterActualWeightDialogOpen === params.id}
                    onClose={() => {
                      enterActualWeightDialogSetOpen('');
                    }}
                    onSubmit={() => {
                      enqueueSnackbar('Actual weight added successfully', { variant: 'success' });
                    }}
                    product={product}
                    orderId={orderId}
                  />
                </>
              )} */}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              {issue ? (
                <Button
                  variant="outlined"
                  LinkComponent={RouterLink}
                  href={paths.dashboard.issues.issueDetails(orderId, issue.issueId)}
                >
                  View Logged Issue
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  disabled={!isIssueLoggable}
                  onClick={() => onReportProductIssue(product.id.toString())}
                >
                  Log Issue
                </Button>
              )}
              <ActionButton
                product={product}
                productsInCart={productsInCart}
                handleAddToCart={handleAddToCart}
                loading={addToCartLoading || recalculatingLocalProducts}
              />
            </Stack>
            <ReportProductIssueDialog
              open={reportProductIssueDialogOpen === product.id}
              onClose={() => onReportProductIssue('')}
              onSubmit={() => {
                refetchOrderIssues();
              }}
              orderId={orderId}
              productId={product.id}
              productName={product.productName}
              productUnits={product.units}
              productCost={product.price}
              productQuantity={+product.requestedQuantity}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default function PreviousOrderDetails() {
  const settings = useSettingsContext();
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const { mutate: addToCart, isLoading: addToCartLoading } = useAddToCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [reportOrderIssueDialogOpen, reportOrderIssueDialogSetOpen] = useState(false);
  const [reportProductIssueDialogOpen, reportProductIssueDialogSetOpen] = useState('');
  const [enterActualWeightDialogOpen, enterActualWeightDialogSetOpen] = useState('');
  const routeNavigate = routeApi.useNavigate();
  const routeSearch = routeApi.useSearch();
  const { sortModel: searchSortModel } = getTableModelsFromSearchParams(routeSearch);

  const [sortModel, setSortModel] = useState<GridSortModel>(searchSortModel ?? []);

  useSyncTableSearchParams({ navigate: routeNavigate, sortModel });

  const { data: orderDetails } = useGetOrderDetails(orderId ?? '', sortModel[0]);
  const { data: orderIssues, refetch: refetchOrderIssues } = useGetOrderIssuesForOrder(
    orderId ?? ''
  );
  const { mutate: downloadOrderPdf, isLoading: isDownloadingOrderPdf } = useDownloadOrderPdf();

  const handleDownloadPdf = () => {
    if (!orderDetails) return;

    downloadOrderPdf(
      { pathParams: { orderId } },
      {
        onSuccess: (blob) =>
          downloadBlob(blob, `order_${orderDetails.orderReferenceNumber}.pdf`),
      }
    );
  };

  const { data: cart } = useGetCart();
  // state variable to fix the glitchy transition between add to basket and update buttons
  const [recalculatingLocalProducts, setRecalculatingLocalProducts] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const handleAddToCart = useCallback(
    (productStockId: string, quantity: number) => {
      setRecalculatingLocalProducts(true);
      addToCart(
        {
          body: {
            productStockId,
            quantity,
          },
        },
        {
          onSuccess: (response) => {
            enqueueSnackbar('Successfully added to basket');
            // TODO: hack remove second key entry from useGetCart api hook
            // This is because of left over EditOrder Logic
            queryClient.setQueryData(['cart'], response);
            setRecalculatingLocalProducts(false);
            // queryClient.refetchQueries(['orders']);
          },
        }
      );
    },
    [addToCart, enqueueSnackbar, queryClient]
  );

  const productsInCart = useMemo(() => cart?.baskets?.flatMap((basket) => basket.items), [cart]);

  const navigate = useNavigate();

  const orderLinkedIssue = orderIssues?.find(
    (issue) => issue.orderId === orderDetails?.id && !issue.buyerOrderProductId
  );
  const getProductIssuesDictionary = () => {
    if (!orderIssues) return {};

    return orderIssues.reduce(
      (acc, issue) => {
        if (issue.buyerOrderProductId) {
          acc[issue.buyerOrderProductId] = issue;
        }
        return acc;
      },
      {} as Record<string, OrderIssueModel>
    );
  };

  const productIssuesDictionary = getProductIssuesDictionary();

  const deliveryDate = orderDetails?.deliveryDate ? new Date(orderDetails?.deliveryDate) : null;

  const issueLoggableDeadline = deliveryDate ? new Date(deliveryDate) : null;
  if (issueLoggableDeadline) issueLoggableDeadline.setDate(issueLoggableDeadline.getDate() + 2);

  const todayDate = new Date();
  // Check that the deadline for logging issues has not passed and that the order has been delivered.
  const isBeforeIssueLogDeadline = issueLoggableDeadline ? issueLoggableDeadline > todayDate : null;
  const orderHasBeenDelivered = deliveryDate ? todayDate >= deliveryDate : null;
  const isIssueLoggable = isBeforeIssueLogDeadline && orderHasBeenDelivered;

  let issueNotLoggableText: string;
  if (orderHasBeenDelivered === true) {
    issueNotLoggableText = 'You are no longer able to';
  } else if (orderHasBeenDelivered === false) {
    issueNotLoggableText = 'You are not yet able to';
  } else {
    issueNotLoggableText = 'You are not able to';
  }

  const columns: GridColDef[] = [
    {
      field: 'productName',
      headerName: 'Product Name',
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          onClick={() =>
            navigate({
              to: paths.dashboard.products.productDetails(params.row.productStockId),
            })
          }
        >
          <Typography variant="body2" color="textPrimary" align="left">
            {params.value}
          </Typography>
          <Typography variant="body2" color="GrayText" align="left">
            {params.row.units}
          </Typography>
        </Link>
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      renderCell: (params) => `£${fPriceString(params.value)}`,
    },
    { field: 'requestedQuantity', headerName: 'Quantity', sortable: false },
    {
      field: 'weight',
      headerName: 'Weight',
      sortable: false,
      renderCell: (params) => (
        <Stack direction="column">
          {params.row.estimatedWeight ? (
            <>
              <span style={{ display: 'block' }}>
                {params.row.receivedWeight ?? params.row.estimatedWeight} kg
              </span>
              {orderDetails && !params.row.receivedWeight && (
                <>
                  <Link
                    onClick={() => enterActualWeightDialogSetOpen(params.id.toString())}
                    sx={{ cursor: 'pointer' }}
                    color={theme.palette.primary.dark}
                  >
                    Enter actual weight
                  </Link>

                  <ActualWeightDialog
                    open={enterActualWeightDialogOpen === params.id}
                    onClose={() => {
                      enterActualWeightDialogSetOpen('');
                    }}
                    onSubmit={() => {
                      enqueueSnackbar('Actual weight added successfully', { variant: 'success' });
                    }}
                    product={params.row}
                    orderId={orderDetails?.id}
                  />
                </>
              )}
            </>
          ) : (
            'N/A'
          )}
        </Stack>
      ),
      minWidth: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => (
        <Stack spacing={1} direction="row">
          {productIssuesDictionary[params.id.toString()] ? (
            <Button
              variant="outlined"
              LinkComponent={RouterLink}
              href={paths.dashboard.issues.issueDetails(
                orderId,
                productIssuesDictionary[params.id.toString()].issueId
              )}
            >
              View Logged Issue
            </Button>
          ) : (
            <Button
              variant="outlined"
              disabled={!isIssueLoggable}
              onClick={() => reportProductIssueDialogSetOpen(params.id.toString())}
            >
              Log Issue
            </Button>
          )}
          <ActionButton
            product={params.row}
            productsInCart={productsInCart}
            handleAddToCart={handleAddToCart}
            loading={addToCartLoading || recalculatingLocalProducts}
          />
          {!!orderDetails && (
            <ReportProductIssueDialog
              open={reportProductIssueDialogOpen === params.id}
              onClose={() => reportProductIssueDialogSetOpen('')}
              onSubmit={() => {
                refetchOrderIssues();
              }}
              orderId={orderDetails.id}
              productId={params.id.toString()}
              productName={params.row.productName}
              productUnits={params.row.units}
              productCost={params.row.price}
              productQuantity={+params.row.requestedQuantity}
            />
          )}
        </Stack>
      ),
      minWidth: 300,
    },
  ];

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    // Only keep the last sort model item if multiple are provided
    const singleFieldSortModel = newSortModel.slice(-1);
    setSortModel(singleFieldSortModel);
  };

  const getOrderProducts = () => {
    if (orderDetails) {
      return isMobile ? (
        orderDetails.products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            isIssueLoggable={isIssueLoggable}
            reportProductIssueDialogOpen={reportProductIssueDialogOpen}
            onReportProductIssue={reportProductIssueDialogSetOpen}
            orderId={orderDetails.id}
            refetchOrderIssues={refetchOrderIssues}
            issue={productIssuesDictionary[product.id.toString()]}
            productsInCart={productsInCart}
            handleAddToCart={handleAddToCart}
            addToCartLoading={addToCartLoading}
            recalculatingLocalProducts={recalculatingLocalProducts}
          />
        ))
      ) : (
        <DataGrid
          columns={columns}
          rows={orderDetails.products}
          sortingMode="server"
          sortModel={sortModel}
          rowHeight={60}
          onSortModelChange={handleSortModelChange}
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
          pageSizeOptions={[]}
          hideFooterPagination
          disableColumnMenu
        />
      );
    }

    return (
      <Stack direction="column" spacing={2}>
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
      </Stack>
    );
  };

  return (
    <ACHelmetContainer
      title={`Order Number #${orderDetails?.orderReferenceNumber}`}
      maxWidth={settings.themeStretch ? false : 'xl'}
    >
      <GoBackHeader
        heading="Order Details"
        action={
          <Stack direction="row" gap={2}>
            {orderLinkedIssue ? (
              <Button
                variant="outlined"
                LinkComponent={RouterLink}
                href={paths.dashboard.issues.issueDetails(orderId, orderLinkedIssue.issueId)}
              >
                View Logged Order Issue
              </Button>
            ) : (
              <Button
                variant="outlined"
                disabled={!isIssueLoggable}
                onClick={() => reportOrderIssueDialogSetOpen(true)}
              >
                Log Issue with Order
              </Button>
            )}
            <LoadingButton
              variant="outlined"
              startIcon={<Iconify icon="eva:download-outline" />}
              loading={isDownloadingOrderPdf}
              disabled={!orderDetails}
              onClick={handleDownloadPdf}
            >
              Download PDF
            </LoadingButton>
          </Stack>
        }
      />
      <Card sx={{ p: 3, mb: 2 }}>
        <Box sx={{ mb: 3, p: 2 }}>
          <Stack direction="column" spacing={1}>
            <Typography variant="h2">
              {orderDetails?.orderReferenceNumber ? (
                `Order Number #${orderDetails?.orderReferenceNumber}`
              ) : (
                <Skeleton width={200} />
              )}
            </Typography>
            <Typography component="h3" gutterBottom>
              {orderDetails ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 6, md: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    <Typography component="span" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Order Created:
                    </Typography>
                    <br />
                    {new Date(orderDetails.createdOn).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    <Typography component="span" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Ordered by:
                    </Typography>
                    <br />
                    {orderDetails.orderedByUserEmail}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    <Typography component="span" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Order Reference:
                    </Typography>
                    <br />
                    {orderDetails.orderReference || 'N/A'}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Skeleton width={150} />
                  <Skeleton width={150} />
                  <Skeleton width={150} />
                </Stack>
              )}
            </Typography>
            <Typography gutterBottom>Below is a detailed breakdown of your order.</Typography>
            {!isIssueLoggable && (
              <Alert severity="warning">
                {`${issueNotLoggableText} log any issues with this order. Please contact the supplier if you have any issues.`}
              </Alert>
            )}
          </Stack>
        </Box>
        {getOrderProducts()}
      </Card>
      {!!orderDetails && (
        <ReportOrderIssueDialog
          open={reportOrderIssueDialogOpen}
          orderId={orderDetails.id}
          onClose={() => reportOrderIssueDialogSetOpen(false)}
          onSubmit={() => {
            refetchOrderIssues();
          }}
        />
      )}
    </ACHelmetContainer>
  );
}
